import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '@/assets/styles/colors';
import { Text } from '@/components/ui/text/Text';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import type { SimpleFilter } from '@/components/ui/filters/FilterDropdown';
import { SCREEN_PREFERENCE_CODES } from '@/routes';
import { areaList, type AreaListItem, type AreaStatusFilter } from '@/api/domains/organization/areas';
import { useUI } from '@/hooks/ui/useUI';
import { useAreaEnableDisable } from './useAreaEnableDisable';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { SECTIONS } from '@/types/sections';
import { formatToDdMmYyyyHHmm } from '@/utils';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';


interface AreasFiltersState {
  status: AreaStatusFilter;
}

interface UseAreasListResult {
  rows: AreaListItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  filters: AreasFiltersState;
  setFilters: Dispatch<SetStateAction<AreasFiltersState>>;
  filterConfig: SimpleFilter[];
  columns: TableColumn<AreaListItem>[];
  actions: TableAction<AreaListItem>[];
  onSearch: (term: string) => void;
  openCreatePanel: () => void;
  loading: boolean;
  refetch: () => void;
}

export function useAreasList(): UseAreasListResult {
  const { t: translate } = useTranslation();
  const { openSidePanel, showApiError } = useUI();
  const { canSection } = usePagePermissions();
  const canCreateAreas = canSection(SECTIONS.AREAS, 'create');
  const canEditAreas = canSection(SECTIONS.AREAS, 'edit');
  const canToggleAreas = canSection(SECTIONS.AREAS, 'delete');
  const { enableArea, disableArea } = useAreaEnableDisable();
  const [togglingIds, setTogglingIds] = useState<Set<number>>(new Set());

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<AreasFiltersState>,
      _signal: AbortSignal,
    ): Promise<PaginatedFetchResult<AreaListItem> | null> => {
      const response = await areaList.list({
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText,
        status: params.filters.status,
      });
      if (!response.success || !response.data) {
        showApiError(response, translate('organization:messages.areasLoadError'));
        return null;
      }
      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    filters,
    setFilters,
    refetch,
  } = usePaginatedList<AreaListItem, AreasFiltersState>({
    fetchFn,
    initialFilters: { status: 'all' as AreaStatusFilter },
    screenCode: SCREEN_PREFERENCE_CODES.ORGANIZACION_AREAS,
  });

  const toggleAreaStatus = useCallback(async (row: AreaListItem) => {
    setTogglingIds((previous) => new Set(previous).add(row.id));

    const success = row.isEnabled
      ? await disableArea(row.id)
      : await enableArea(row.id);

    setTogglingIds((previous) => {
      const next = new Set(previous);
      next.delete(row.id);
      return next;
    });

    if (success) {
      refetch();
    }
  }, [disableArea, enableArea, refetch]);

  useEffect(() => {
    const handleAreaFormSuccess = () => { refetch(); };
    window.addEventListener('area-form-success', handleAreaFormSuccess);
    return () => {
      window.removeEventListener('area-form-success', handleAreaFormSuccess);
    };
  }, [refetch]);

  const openCreatePanel = useCallback(() => {
    if (!canCreateAreas) {
      return;
    }

    openSidePanel({
      component: 'AreaFormPanel',
      title: translate('organization:panel.createTitle'),
      props: {
        record: {
          mode: 'create',
        },
      },
    });
  }, [canCreateAreas, openSidePanel, translate]);

  const openEditPanel = useCallback((areaId: number) => {
    if (!canEditAreas) {
      return;
    }

    openSidePanel({
      component: 'AreaFormPanel',
      title: translate('organization:panel.editTitle'),
      props: {
        record: {
          mode: 'edit',
          areaId,
        },
      },
    });
  }, [canEditAreas, openSidePanel, translate]);

  const filterConfig: SimpleFilter[] = useMemo(() => [
    {
      key: 'status',
      label: translate('organization:filters.status'),
      options: [
        { value: 'all', label: translate('organization:filters.all') },
        { value: 'enabled', label: translate('organization:filters.enabled') },
        { value: 'disabled', label: translate('organization:filters.disabled') },
      ],
      selectedValue: filters.status,
      onChange: (value: string) => {
        setFilters((previous) => ({
          ...previous,
          status: value as AreaStatusFilter,
        }));
      },
    },
  ], [filters.status, setFilters, translate]);

  const columns: TableColumn<AreaListItem>[] = useMemo(
    () => [
      {
        key: 'id',
        header: translate('organization:fields.id'),
        sortable: true,
        render: (row: AreaListItem) => row.id,
        width: 80,
      },
      {
        key: 'code',
        header: translate('organization:fields.areaCode'),
        sortable: true,
        render: (row: AreaListItem) => row.code,
      },
      {
        key: 'name',
        header: translate('organization:fields.areaName'),
        sortable: true,
        render: (row: AreaListItem) => row.name,
      },
      {
        key: 'parentAreaName',
        header: translate('organization:fields.parentArea'),
        sortable: true,
        render: (row: AreaListItem) => row.parentAreaName || '-',
      },
      {
        key: 'createdDate',
        header: translate('common:fields.createdDate'),
        sortable: true,
        render: (row: AreaListItem) => formatToDdMmYyyyHHmm(row.createdDate) || '-',
      },
      {
        key: 'modifiedDate',
        header: translate('common:fields.modifiedDate'),
        sortable: true,
        render: (row: AreaListItem) => formatToDdMmYyyyHHmm(row.modifiedDate) || '-',
      },
      {
        key: 'status',
        header: translate('organization:fields.status'),
        sortable: true,
        render: (row: AreaListItem) => (
          <Text
            variant="body-regular"
            style={{
              color: row.isEnabled ? colors.feedback.success400 : colors.grays.neutral99,
              fontWeight: 500,
            }}
          >
            {row.isEnabled
              ? translate('organization:status.enabled')
              : translate('organization:status.disabled')}
          </Text>
        ),
      },
    ],
    [translate],
  );

  const actions: TableAction<AreaListItem>[] = useMemo(
    () => [
      {
        id: 'edit',
        label: translate('common:tableActions.edit'),
        icon: 'edit',
        hidden: () => !canEditAreas,
        onClick: (row: AreaListItem) => {
          openEditPanel(row.id);
        },
      },
      {
        id: 'enable',
        label: translate('common:tableActions.enable'),
        icon: 'check',
        onClick: (row: AreaListItem) => {
          void toggleAreaStatus(row);
        },
        disabled: (row: AreaListItem) => togglingIds.has(row.id),
        isLoading: (row: AreaListItem) => togglingIds.has(row.id),
        hidden: (row: AreaListItem) => !canToggleAreas || row.isEnabled,
      },
      {
        id: 'disable',
        label: translate('common:tableActions.disable'),
        icon: 'close',
        onClick: (row: AreaListItem) => {
          void toggleAreaStatus(row);
        },
        variant: 'danger',
        disabled: (row: AreaListItem) => togglingIds.has(row.id),
        isLoading: (row: AreaListItem) => togglingIds.has(row.id),
        hidden: (row: AreaListItem) => !canToggleAreas || !row.isEnabled,
      },
    ],
    [canEditAreas, canToggleAreas, openEditPanel, toggleAreaStatus, togglingIds, translate],
  );

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    filterConfig,
    columns,
    actions,
    onSearch,
    openCreatePanel,
    loading,
    refetch,
  };
}
